import axios from "axios";
import type {
  SketchStrokePatch,
  SketchPatch,
  Sketch,
} from "unnamed-contracts/dist/models/sketch";
import { SessionSchema, type Session } from "./types/session";

const RAW_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
if (import.meta.env.DEV && !RAW_API_BASE_URL) {
  console.warn(
    "VITE_API_BASE_URL is not set; defaulting to http://localhost:3065"
  );
}
export const API_BASE_URL = RAW_API_BASE_URL ?? "http://localhost:3065";

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = (err && err.config) || {};
    if (err?.response?.status === 401) {
      // Avoid infinite loop: do not try to refresh if this request opted out
      const noRefresh = original?.headers?.["X-No-Refresh"];
      if (noRefresh) throw err;

      if (!original._retried) {
        original._retried = true;
        try {
          await api.post("/auth/refresh", undefined, {
            headers: { "X-No-Refresh": "1" },
          });
          return api.request(original);
        } catch (e) {
          // Redirect to auth page on refresh failure
          try {
            const path =
              typeof window !== "undefined" ? window.location.pathname : "/";
            if (path !== "/auth") {
              if (typeof window !== "undefined") {
                window.history.pushState({}, "", "/auth");
                window.dispatchEvent(new PopStateEvent("popstate"));
              }
            }
          } catch {}
        }
      }
    }
    throw err;
  }
);

export function startGoogleLogin(redirect = window.location.origin) {
  const url = `${API_BASE_URL}/auth/google/start?redirect=${encodeURIComponent(
    redirect
  )}`;
  window.location.href = url;
}

export async function requestMagicLink(email: string) {
  const { data } = await api.post("/auth/magic/request", { email });
  return data as { ok: boolean; debug?: { token: string; link: string } };
}

export async function completeMagicToken(token: string) {
  try {
    const { data } = await api.post(
      "/auth/magic/consume",
      { token },
      { headers: { "X-No-Refresh": "1" } }
    );
    return data as { ok: boolean; userId?: string; error?: string };
  } catch (err: any) {
    const data = err?.response?.data;
    if (data) return data;
    return { ok: false, error: "request_failed" } as const;
  }
}

export async function logout() {
  const { data } = await api.post("/auth/logout");
  return data as { ok: boolean };
}

export async function getMe() {
  const { data } = await api.get("/auth/me");
  return data as { userId: string };
}

export async function createProject(body: {
  name: string;
  description?: string;
}) {
  const { data } = await api.post("/projects", body);
  return data;
}

export async function loadSession(): Promise<Session> {
  const res = await fetch(`${API_BASE_URL}/auth/session`, {
    credentials: "include",
  });
  const json = await res.json();
  try {
    const isDev = !!(import.meta as any)?.env?.DEV;
    if (isDev) {
      try {
        SessionSchema.parse(json);
      } catch (e) {
        console.warn("Session schema validation failed", e);
      }
    }
  } catch {}
  return json as Session;
}

// Switch current project and hydrate full session
export interface SwitchCurrentProjectBody {
  readonly projectId: string;
  readonly deviceId?: string;
  readonly currentPage?: string;
}

export async function switchCurrentProject(
  body: SwitchCurrentProjectBody
): Promise<Session> {
  const { data } = await api.post("/session/current-project", body);
  try {
    const isDev = !!(import.meta as any)?.env?.DEV;
    if (isDev) {
      try {
        SessionSchema.parse(data);
      } catch (e) {
        console.warn("Session schema validation failed", e);
      }
    }
  } catch {}
  return data as Session;
}

// Project preferences (UI state) updates
export interface ProjectPrefsUpdateBody {
  readonly theme?: "light" | "dark";
  readonly currentPage?: string;
  readonly currentSketchId?: string;
  readonly currentBreakpoint?: string;
  readonly currentLanguage?: string;
  readonly stagePosition?: { x: number; y: number };
  readonly zoomMode?: string;
  readonly zoomValue?: number;
  readonly helpers?: {
    readonly rulers?: boolean;
    readonly snap?: boolean;
    readonly guides?: boolean;
    readonly dots?: boolean;
    readonly lines?: boolean;
  };
  readonly audioMuted?: boolean;
  readonly selectedMode?: "sketch" | "edit" | "ds" | "cms";
  readonly selectedTool?: string;
  readonly sketchEraserMode?: "delete" | "delete-clean";
  readonly sketchSelectionMode?: "marquee" | "lasso";
  readonly sourceId?: string;
}

export async function updateProjectPrefs(
  projectId: string,
  body: ProjectPrefsUpdateBody
) {
  const { data } = await api.post(`/prefs/project/${projectId}`, body);
  return data as { ok: boolean };
}

export interface UserPrefsUpdateBody {
  readonly sourceId?: string;
  readonly [key: string]: unknown;
}

export async function updateUserPrefs(body: UserPrefsUpdateBody) {
  const { data } = await api.post(`/prefs/me`, body);
  return data as { ok: boolean };
}

export async function createSketch(
  projectId: string,
  body: { title: string }
) {
  const { data } = await api.post(`/projects/${projectId}/sketches`, body);
  return data as { ok: boolean; sketch?: Sketch; error?: string };
}

export async function getSketch(projectId: string, sketchId: string) {
  const { data } = await api.get(
    `/projects/${projectId}/sketches/${sketchId}`
  );
  return data as { ok: boolean; sketch?: Sketch; error?: string };
}

export async function updateSketch(
  projectId: string,
  sketchId: string,
  body: { title?: string }
) {
  try {
    const { data } = await api.patch(
      `/projects/${projectId}/sketches/${sketchId}`,
      body
    );
    return data as { ok: boolean; sketch?: Sketch; error?: string };
  } catch (error: any) {
    const data = error?.response?.data;
    if (data && typeof data === "object") {
      return data as { ok: boolean; sketch?: Sketch; error?: string };
    }
    return { ok: false, error: "request_failed" } as const;
  }
}

export async function applySketchStrokePatch(
  projectId: string,
  sketchId: string,
  body: SketchStrokePatch,
  options?: { socketId?: string }
) {
  const config = options?.socketId
    ? { headers: { "x-socket-id": options.socketId } }
    : undefined;
  const { data } = await api.patch(
    `/projects/${projectId}/sketches/${sketchId}/strokes`,
    body,
    config
  );
  return data as { ok: boolean; version?: number; error?: string };
}

export async function applySketchPatch(
  projectId: string,
  sketchId: string,
  body: SketchPatch,
  options?: { socketId?: string }
) {
  const config = options?.socketId
    ? { headers: { "x-socket-id": options.socketId } }
    : undefined;
  const { data } = await api.patch(
    `/projects/${projectId}/sketches/${sketchId}/patch`,
    body,
    config
  );
  return data as { ok: boolean; version?: number; error?: string };
}
